import { useEffect, useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { highlightMatch } from "../lib/highlightMatch";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Wrench, Send, Paperclip, Trash2, Users, Building2, Tag, X, Info, AlertTriangle, AlertOctagon, FileText, Download, Mail, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const CATS_FALLBACK = ["Elettrica", "Idraulica", "Muraria", "Serramenti/Fabbro", "Ascensore", "Riscaldamento/Caldaia", "Tetto/Lattoneria", "Pulizie", "Giardinaggio", "Altro"];
const PRIO = {
  Info: { cls: "bg-sky-100 text-sky-800 border-sky-200", icon: Info },
  Importante: { cls: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle },
  Urgente: { cls: "bg-red-100 text-red-800 border-red-200", icon: AlertOctagon },
};
const fmtDate = (s) => new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
const emailSubject = (a) => `[Ginius · ${a.priorita}] ${a.titolo}`;
// Iter122dd · firma email dinamica dal Titolare del trattamento del tenant
const emailBody = (a, tit) => {
  const firma = ((tit && tit.ragione_sociale) || "").trim() || "lo Studio";
  return `Comunicazione da ${firma}\n\n${a.titolo}\n\n${a.testo}\n\nAccedi all'app per i dettagli.`;
};

function PrioBadge({ p }) {
  const cfg = PRIO[p] || PRIO.Info; const I = cfg.icon;
  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold border flex items-center gap-1 ${cfg.cls}`}><I className="w-3.5 h-3.5" /> {p}</span>;
}

function Allegati({ list }) {
  if (!list?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {list.map((a) => (
        <button type="button" key={a.id} onClick={() => openSecureFile(`/files/${a.id}`)} data-testid={`forn-allegato-${a.id}`}
          className="inline-flex items-center gap-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-1.5 text-slate-700">
          {(a.tipo || "").includes("pdf") ? <FileText className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}
          <span className="max-w-[180px] truncate">{a.nome || "Allegato"}</span>
          <Download className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
}

export default function ComunicazioniFornitori() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center"><Wrench className="w-7 h-7 text-white" /></div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Comunicazioni fornitori</h1>
            <InfoHint section="comunicazioni-fornitori" />
          </div>
          <p className="text-base text-slate-500">{isAdmin ? "Invia messaggi e avvisi ai fornitori e consulta lo storico" : "Le comunicazioni dello Studio per te"}</p>
        </div>
      </div>
      {isAdmin ? <AdminView /> : <FornitoreView />}
    </div>
  );
}

function FornitoreView() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/avvisi-fornitori/miei").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (list.length === 0) return <Card className="p-5 text-center text-slate-500" data-testid="forn-avvisi-empty">Nessuna comunicazione al momento.</Card>;
  return (
    <div className="space-y-4" data-testid="forn-avvisi-list">
      {list.map((a) => (
        <Card key={a.id} className="p-5 border-2" data-testid={`forn-avviso-${a.id}`}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="font-bold text-xl text-slate-800">{a.titolo}</h3>
            <PrioBadge p={a.priorita} />
          </div>
          <div className="text-xs text-slate-400 mb-3">{fmtDate(a.created_at)}</div>
          <p className="text-base text-slate-700 whitespace-pre-wrap">{a.testo}</p>
          <Allegati list={a.allegati} />
        </Card>
      ))}
    </div>
  );
}

function AdminView() {
  const { user } = useAuth();
  const empty = { titolo: "", testo: "", priorita: "Info", scope: "tutti" };
  const [form, setForm] = useState(empty);
  const [condomini, setCondomini] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [selCat, setSelCat] = useState(new Set());
  const [selCond, setSelCond] = useState(new Set());
  const [selForn, setSelForn] = useState(new Set());
  const [allegati, setAllegati] = useState([]);
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [storico, setStorico] = useState([]);
  const [detail, setDetail] = useState(null);
  const [resending, setResending] = useState(false);
  // Iter123k194 · categorie dinamiche dal backend (prima: lista hardcoded di 10 voci)
  const [cats, setCats] = useState(CATS_FALLBACK);
  // Iter123k195 · esclusioni manuali dalla preview (checkbox): il set contiene gli ID
  // che l'utente ha deselezionato nel pannello "Destinatari".
  const [exclSet, setExclSet] = useState(new Set());

  const load = () => api.get("/avvisi-fornitori").then((r) => setStorico(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/meta").then((r) => {
      const c = (r.data && r.data.categorie) || [];
      if (c.length) setCats(c);
    }).catch(() => {});
    api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {});
    api.get("/fornitori", { params: { limit: 5000 } }).then((r) => setFornitori(r.data.fornitori || [])).catch(() => {});
    load();
    const raw = localStorage.getItem("ginius_prefill_avviso_forn");
    if (raw) { try { const p = JSON.parse(raw); setForm((f) => ({ ...f, titolo: p.titolo || "", testo: p.testo || "" })); toast.info("Modello caricato: rivedi e completa l'invio."); } catch {} localStorage.removeItem("ginius_prefill_avviso_forn"); }
  }, []);

  const toggle = (set, fn, id) => { const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); fn(n); };

  // Iter123k195 · reset esclusioni quando l'utente cambia scope/categorie (la preview cambia composizione)
  useEffect(() => { setExclSet(new Set()); }, [form.scope, selCat]);

  // Iter123k194 · Elenco preview dei fornitori che riceveranno la comunicazione.
  // Visibile per scope "tutti" e "categorie" (per "condomini" il match avviene lato backend
  // in base alle manutenzioni svolte, qui non abbiamo quel dato).
  const previewFornitori = (() => {
    if (form.scope === "tutti") return fornitori;
    if (form.scope === "categorie") {
      if (selCat.size === 0) return [];
      return fornitori.filter((f) => (f.categorie || []).some((c) => selCat.has(c)));
    }
    return [];
  })();

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (files.length === 0) return;
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/avvisi/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAllegati((a) => [...a, data]);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito"); }
    e.target.value = "";
  };

  const invia = async () => {
    if (!form.titolo.trim() || !form.testo.trim()) { toast.error("Inserisci titolo e testo"); return; }
    if (form.scope === "categorie" && selCat.size === 0) { toast.error("Seleziona almeno una categoria"); return; }
    if (form.scope === "condomini" && selCond.size === 0) { toast.error("Seleziona almeno un condominio"); return; }
    if (form.scope === "singoli" && selForn.size === 0) { toast.error("Seleziona almeno un fornitore"); return; }
    // Iter123k195 · Se l'utente ha deselezionato alcuni destinatari dalla preview,
    // convertiamo l'invio in modalità "singoli" con la lista effettiva risultante.
    let payload;
    const hasExclusions = (form.scope === "tutti" || form.scope === "categorie") && exclSet.size > 0;
    if (hasExclusions) {
      const finali = previewFornitori.filter((f) => !exclSet.has(f.id)).map((f) => f.id);
      if (finali.length === 0) { toast.error("Hai deselezionato tutti i destinatari"); return; }
      payload = {
        ...form, scope: "singoli",
        categorie: [], condominio_ids: [], fornitore_ids: finali, allegati,
      };
    } else {
      payload = {
        ...form,
        categorie: Array.from(selCat),
        condominio_ids: Array.from(selCond),
        fornitore_ids: Array.from(selForn),
        allegati,
      };
    }
    setSending(true);
    try {
      const { data } = await api.post("/avvisi-fornitori", payload);
      toast.success(`Comunicazione inviata a ${data.destinatari_count} fornitori (${data.email_inviati} email · ${data.notifiche_inviate} notifiche)`);
      setForm(empty); setSelCat(new Set()); setSelCond(new Set()); setSelForn(new Set());
      setExclSet(new Set()); setAllegati([]);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito"); }
    setSending(false);
  };

  const elimina = async (a) => {
    if (!window.confirm("Eliminare questa comunicazione dallo storico?")) return;
    try { await api.delete(`/avvisi-fornitori/${a.id}`); load(); } catch { toast.error("Eliminazione non riuscita"); }
  };

  const reinvia = async (a) => {
    if (!window.confirm("Reinviare questa comunicazione agli stessi fornitori?")) return;
    setResending(true);
    try {
      const { data } = await api.post(`/avvisi-fornitori/${a.id}/reinvia`);
      toast.success(`Comunicazione reinviata (${data.inviati} email · ${data.notifiche} notifiche)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Reinvio non riuscito"); }
    setResending(false);
  };

  const fornFiltered = fornitori.filter((f) => `${f.ragione_sociale} ${f.settore || ""} ${f.citta || ""}`.toLowerCase().includes(search.toLowerCase()));

  return (
    // Iter123k198 · fix overflow mobile: `min-w-0` sui figli del grid consente di
    // restringere oltre la larghezza intrinseca dei form controls (prima: card 619px vs vp 414px)
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="p-4 border-2 h-fit min-w-0 overflow-hidden" data-testid="forn-comunic-compose">
        <h2 className="font-display text-xl font-bold mb-4">Nuova comunicazione</h2>
        <label className="text-sm font-semibold text-slate-600">Titolo<span className="text-red-500 ml-0.5">*</span></label>
        <Input data-testid="forn-avviso-titolo" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="Es. Aggiornamento procedura preventivi" className="h-12 mt-1 mb-3" />
        <label className="text-sm font-semibold text-slate-600">Testo<span className="text-red-500 ml-0.5">*</span></label>
        <Textarea data-testid="forn-avviso-testo" value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })} placeholder="Scrivi il messaggio per i fornitori..." className="min-h-[120px] mt-1 mb-3" />

        <label className="text-sm font-semibold text-slate-600">Priorità</label>
        <div className="flex gap-2 mt-1 mb-4">
          {Object.keys(PRIO).map((p) => (
            <button key={p} type="button" data-testid={`forn-prio-${p}`} onClick={() => setForm({ ...form, priorita: p })}
              className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${form.priorita === p ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-200 text-slate-500"}`}>{p}</button>
          ))}
        </div>

        <label className="text-sm font-semibold text-slate-600">Destinatari</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1 mb-3">
          {[["tutti", "Tutti", Users], ["categorie", "Attività", Tag], ["condomini", "Condominio", Building2], ["singoli", "Singoli", Wrench]].map(([v, l, I]) => (
            <button key={v} type="button" data-testid={`forn-scope-${v}`} onClick={() => setForm({ ...form, scope: v })}
              className={`px-2 py-2.5 rounded-lg text-sm font-semibold border-2 flex items-center justify-center gap-1 ${form.scope === v ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-200 text-slate-500"}`}>
              <I className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>

        {form.scope === "categorie" && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="forn-scope-categorie-list">
            {cats.map((c) => (
              <button key={c} type="button" onClick={() => toggle(selCat, setSelCat, c)}
                className={`px-3 py-1.5 rounded-full border-2 text-sm font-semibold ${selCat.has(c) ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-200 text-slate-500"}`}>{c}</button>
            ))}
          </div>
        )}
        {/* Iter123k194 · Preview dei fornitori selezionati (visibile per scope "tutti" o "categorie")
            Iter123k195 · Con spunta per deselezionare i singoli destinatari prima dell'invio */}
        {(form.scope === "tutti" || (form.scope === "categorie" && selCat.size > 0)) && (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 max-h-56 overflow-auto mb-3 bg-slate-50/60"
            data-testid="forn-destinatari-preview">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Destinatari ({Math.max(0, previewFornitori.length - exclSet.size)}/{previewFornitori.length})
              </div>
              {previewFornitori.length > 0 && (
                <div className="flex items-center gap-2 text-[11px]">
                  <button type="button" data-testid="forn-preview-select-all"
                    onClick={() => setExclSet(new Set())}
                    className="text-brand font-bold hover:underline">Tutti</button>
                  <span className="text-slate-300">·</span>
                  <button type="button" data-testid="forn-preview-select-none"
                    onClick={() => setExclSet(new Set(previewFornitori.map((f) => f.id)))}
                    className="text-slate-500 font-bold hover:underline">Nessuno</button>
                </div>
              )}
            </div>
            {previewFornitori.length === 0 ? (
              <div className="text-sm text-slate-400">Nessun fornitore corrisponde ai filtri scelti.</div>
            ) : (
              <ul className="space-y-1">
                {previewFornitori.map((f) => {
                  const checked = !exclSet.has(f.id);
                  return (
                    <li key={f.id} data-testid={`forn-preview-row-${f.id}`}>
                      <label className="flex items-center gap-2 py-1 cursor-pointer text-sm text-slate-700 hover:bg-white rounded px-1">
                        <input type="checkbox" className="w-4 h-4 accent-brand shrink-0"
                          data-testid={`forn-preview-check-${f.id}`}
                          checked={checked}
                          onChange={() => toggle(exclSet, setExclSet, f.id)} />
                        <span className="truncate">
                          <b className={checked ? "text-slate-800" : "text-slate-400 line-through"}>{f.ragione_sociale}</b>
                          {(f.categorie && f.categorie.length > 0) && (
                            <span className="text-slate-400 text-xs"> · {(f.categorie || []).join(", ")}</span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {form.scope === "condomini" && (
          <div className="border-2 rounded-lg p-3 max-h-48 overflow-auto mb-3 space-y-1" data-testid="forn-scope-condomini-list">
            {condomini.map((c) => (
              <label key={c.id} className="flex items-center gap-2 py-1 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-brand" checked={selCond.has(c.id)} onChange={() => toggle(selCond, setSelCond, c.id)} />
                <span className="text-base">{c.nome}</span>
              </label>
            ))}
            <p className="text-xs text-slate-400 pt-1">Riceveranno i fornitori che hanno lavorato su manutenzioni di questi condomìni.</p>
          </div>
        )}
        {form.scope === "singoli" && (
          <div className="mb-3">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca fornitore..." className="h-10 mb-2" />
            <div className="border-2 rounded-lg p-3 max-h-48 overflow-auto space-y-1" data-testid="forn-scope-singoli-list">
              {fornFiltered.map((f) => (
                <label key={f.id} className="flex items-center gap-2 py-1 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-brand" checked={selForn.has(f.id)} onChange={() => toggle(selForn, setSelForn, f.id)} />
                  <span className="text-base">{highlightMatch(f.ragione_sociale, search)} {f.settore && <span className="text-slate-400 text-sm">· {highlightMatch(f.settore, search)}</span>}</span>
                </label>
              ))}
              {fornFiltered.length === 0 && <div className="text-sm text-slate-400">Nessun fornitore trovato.</div>}
            </div>
          </div>
        )}

        {allegati.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {allegati.map((a, i) => (
              <span key={a.id} className="inline-flex items-center gap-2 text-sm bg-slate-100 rounded-lg px-3 py-1.5">
                <Paperclip className="w-4 h-4" /> <span className="max-w-[140px] truncate">{a.nome}</span>
                <button onClick={() => setAllegati(allegati.filter((_, j) => j !== i))}><X className="w-4 h-4 text-slate-500" /></button>
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-2">
          <label data-testid="forn-avviso-allega" className="inline-flex items-center gap-2 h-12 px-4 rounded-md border-2 cursor-pointer text-slate-600 hover:bg-slate-50">
            <Paperclip className="w-5 h-5" /> Allega (PDF/immagine)
            <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={upload} />
          </label>
          <Button data-testid="forn-avviso-invia" disabled={sending} onClick={invia} className="h-12 flex-1 min-w-[160px] bg-brand hover:bg-brand-dark font-bold">
            <Send className="w-5 h-5 mr-2" /> {sending ? "Invio..." : "Invia comunicazione"}
          </Button>
        </div>
      </Card>

      <div data-testid="forn-comunic-storico" className="min-w-0">
        <h2 className="font-display text-xl font-bold mb-4">Storico comunicazioni ({storico.length})</h2>
        <div className="space-y-3">
          {storico.length === 0 && <Card className="p-5 text-center text-slate-500">Nessuna comunicazione inviata.</Card>}
          {storico.map((a) => (
            <Card key={a.id} role="button" tabIndex={0} onClick={() => setDetail(a)}
              onKeyDown={(e) => { if (e.key === "Enter") setDetail(a); }}
              className="p-4 border-2 cursor-pointer transition-colors hover:border-brand hover:bg-[#FBEEF3]/40" data-testid={`forn-storico-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold text-lg text-slate-800">{a.titolo}</h3>
                <div className="flex items-center gap-2 shrink-0">
                  <PrioBadge p={a.priorita} />
                  <button data-testid={`forn-del-avviso-${a.id}`} onClick={(e) => { e.stopPropagation(); elimina(a); }} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-1 text-sm font-semibold text-brand" data-testid={`forn-storico-dest-${a.id}`}>
                <Users className="w-4 h-4 shrink-0" />
                <span className="truncate">A: {a.destinatari_label}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap">{a.testo}</p>
              <div className="text-xs text-slate-400 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>{fmtDate(a.created_at)}</span>
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {a.destinatari_count} destinatari</span>
                {a.allegati?.length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> {a.allegati.length} allegato/i</span>}
                <span className="ml-auto flex items-center gap-1 font-semibold text-brand">Apri <ChevronRight className="w-3.5 h-3.5" /></span>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="forn-avviso-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-2xl font-display pr-6">
                  <Mail className="w-6 h-6 text-brand shrink-0" /> {detail.titolo}
                </DialogTitle>
                <DialogDescription className="sr-only">Dettaglio comunicazione inviata ai fornitori</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <PrioBadge p={detail.priorita} />
                <span className="text-xs text-slate-400">{fmtDate(detail.created_at)}</span>
                {detail.created_by && <span className="text-xs text-slate-400">· da {detail.created_by}</span>}
              </div>
              <div className="rounded-xl border-2 border-slate-100 overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 text-sm space-y-1 border-b">
                  <div className="flex gap-2"><span className="font-semibold text-slate-500 w-20 shrink-0">Destinatari</span><span className="text-slate-800 font-semibold" data-testid="forn-detail-destinatari">{detail.destinatari_label} ({detail.destinatari_count})</span></div>
                  {detail.destinatari_count > 1 && detail.destinatari_nomi?.length > 0 && (
                    <details className="group" data-testid="forn-detail-destinatari-dropdown">
                      <summary className="cursor-pointer font-semibold text-brand flex items-center gap-1 list-none select-none">
                        <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" /> Visualizza elenco destinatari ({detail.destinatari_nomi.length})
                      </summary>
                      <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="forn-detail-destinatari-lista">
                        {detail.destinatari_nomi.map((n, i) => (
                          <span key={i} className="inline-block rounded-full bg-white border border-slate-200 px-2 py-0.5 text-slate-700">{n}</span>
                        ))}
                      </div>
                    </details>
                  )}
                  <div className="flex gap-2"><span className="font-semibold text-slate-500 w-20 shrink-0">Oggetto</span><span className="text-slate-800">{emailSubject(detail)}</span></div>
                </div>
                <div className="px-4 py-4 text-base text-slate-700 whitespace-pre-wrap" data-testid="forn-detail-corpo">{emailBody(detail, user?.titolare_trattamento)}</div>
              </div>
              {detail.allegati?.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-semibold text-slate-600 mb-1">Allegati</div>
                  <Allegati list={detail.allegati} />
                </div>
              )}
              <div className="mt-5 flex justify-end">
                <Button data-testid="forn-avviso-reinvia-btn" disabled={resending} onClick={() => reinvia(detail)}
                  className="bg-brand hover:bg-brand-dark font-bold">
                  <Send className="w-4 h-4 mr-2" /> {resending ? "Reinvio..." : "Reinvia agli stessi fornitori"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
